<template>
  <div
    :id="`${source}_add_phone_block_list_section`"
    class="flex flex-col gap-6 text-white font-medium"
  >
    <div class="flex flex-col gap-2">
      <label class="text-sm secondary_color">Number</label>
      <vue-tel-input
        v-model="internalFormData.phoneNumber"
        :defaultCountry="defaultCountryCode"
        :dropdownOptions="{
          showFlags: true,
          showDialCodeInList: true,
          showSearchBox: true,
          searchBoxPlaceholder: 'Search country..',
        }"
        mode="international"
        :inputOptions="{
          placeholder: 'Enter phone number',
        }"
        :class="{ 'error-border': !!errors.phoneNumber }"
        @blur="validatePhoneNumberInput"
        @update:model-value="handlePhoneNumberChange"
      />
      <span
        v-if="errors.phoneNumber"
        class="text-xs error_message_color mt-1"
      >
        {{ errors.phoneNumber }}
      </span>
    </div>

    <div v-if="phoneCheckStatusValue === phoneCheckStatus.CHECKING">
      <SkeletonLoader :page="loaderPages.NUMBER_CHECK" />
    </div>

    <div v-if="showNameField" class="flex flex-col gap-2">
      <label for="contact-name" class="text-sm secondary_color">Name</label>
      <CustomInput
        id="contact-name"
        v-model="internalFormData.name"
        format="text"
        placeholder="Enter contact name"
        type="primary"
        :isError="!!errors.name"
        :errorMessage="errors.name"
        :disabled="phoneCheckStatusValue === phoneCheckStatus.CHECKING || (!!existingContact && !userClearedName)"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label for="dnd-status" class="text-sm secondary_color">DND status</label>
      <a-select
        id="dnd-status"
        v-model:value="internalFormData.dndStatus"
        class="w-full"
        placeholder="Select status"
        :options="dndStatusOptions"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW_GREY"
            alt="bottom_grey_arrow"
            class="size-4"
          />
        </template>
      </a-select>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { VueTelInput } from "vue-tel-input";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { dndStatusOptions, validationEntities, phoneCheckStatus } from "@/src/constants/callConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { validatePhoneNumber } from "@/src/utils/callsUtils.js";
import { cleanPhoneNumber, isValidPhone, getCountryCode } from "@/src/utils/phoneNumberUtils.js";
import CallService from "@/src/api/CallService.js";
import debounce from "lodash/debounce";

const store = useStore();
const defaultCountryCode = computed(() => store.getters["workspace/defaultCountryCode"]);

onMounted(() => {
  store.dispatch("workspace/fetchDefaultCountryCode");
});

const props = defineProps({
  source: { type: String, required: true },
  formData: {
    type: Object,
    required: true,
    default: () => ({
      phoneNumber: "",
      name: "",
      dndStatus: "do_not_call",
    }),
  },
});

const emit = defineEmits(["update:formData", "validateForm"]);

const internalFormData = reactive({ ...props.formData });
const errors = reactive({
  phoneNumber: "",
  name: "",
});
const existingContact = ref(null);
const phoneCheckStatusValue = ref(phoneCheckStatus.IDLE); 
const userClearedName = ref(false);

const showNameField = computed(() => {
  return internalFormData.phoneNumber.trim() !== "" && 
         !errors.phoneNumber &&
         phoneCheckStatusValue.value === phoneCheckStatus.COMPLETE;
});

const isFormValid = computed(() => {
  const hasValidPhone = internalFormData.phoneNumber.trim() !== "" && !errors.phoneNumber;
  const hasValidName = internalFormData.name.trim() !== "";
  
  return hasValidPhone && hasValidName && !errors.name;
});

const checkPhoneNumberExists = async () => {
  const phoneNumber = internalFormData.phoneNumber.trim();
  
  if (!phoneNumber || errors.phoneNumber) {
    existingContact.value = null;
    phoneCheckStatusValue.value = phoneCheckStatus.IDLE;
    return;
  }
  
  const countryCode = getCountryCode(phoneNumber);
  if (!isValidPhone(phoneNumber, countryCode)) {
    phoneCheckStatusValue.value = phoneCheckStatus.IDLE;
    return;
  }
  
  phoneCheckStatusValue.value = phoneCheckStatus.CHECKING;
  
  try {
    const cleanedNumber = cleanPhoneNumber(phoneNumber);
    
    const response = await CallService.fetchPhoneNumberDetails({
      phone_number: cleanedNumber
    });
    
    if (response?.success && response?.details) {
      const contact = response.details;
      existingContact.value = {
        ...contact,
        name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || 'Unknown',
        entity_type: contact.type === entityTypes.LEAD ? 'Lead' : 'Contact'
      };
      internalFormData.name = existingContact.value.name;
    } else {
      existingContact.value = null;
      internalFormData.name = "";
    }
    phoneCheckStatusValue.value = phoneCheckStatus.COMPLETE;
  } catch (error) {
    existingContact.value = null;
    phoneCheckStatusValue.value = phoneCheckStatus.IDLE;
  }
};

const debouncedCheckNumber = debounce(checkPhoneNumberExists, 500);

const handlePhoneNumberChange = (value) => {
  internalFormData.phoneNumber = value;
  validatePhoneNumberInput();
  userClearedName.value = false;
  existingContact.value = null;
  internalFormData.name = "";
  
  if (!errors.phoneNumber && value.trim()) {
    phoneCheckStatusValue.value = phoneCheckStatus.IDLE;
    debouncedCheckNumber();
  } else {
    phoneCheckStatusValue.value = phoneCheckStatus.IDLE;
  }
};

const validatePhoneNumberInput = () => {
  errors.phoneNumber = "";
  
  const phoneNumber = internalFormData.phoneNumber.trim();
  
  if (!phoneNumber) {
    errors.phoneNumber = "Phone number is required";
    return false;
  }

  const countryCode = getCountryCode(phoneNumber);
  if (!isValidPhone(phoneNumber, countryCode)) {
    errors.phoneNumber = "Please enter a valid phone number for the selected country";
    return false;
  }
  const cleanedNumber = cleanPhoneNumber(phoneNumber);
  const validationResult = validatePhoneNumber(cleanedNumber, validationEntities.NORMAL_CALL);
  
  if (!validationResult.valid) {
    errors.phoneNumber = validationResult.error;
    return false;
  }
  
  return true;
};

watch(
  () => props.formData,
  (newVal) => {
    Object.assign(internalFormData, newVal);
  },
  { deep: true },
);

const validateName = () => {
  errors.name = "";
  
  if (showNameField.value && !existingContact.value && !internalFormData.name.trim()) {
    errors.name = "Name is required";
    return false;
  }
  
  return true;
};

watch(
  () => internalFormData.name,
  (newVal, oldVal) => {
    if (existingContact.value && oldVal && !newVal) {
      userClearedName.value = true;
      existingContact.value = null;
    }
  }
);

watch(
  () => internalFormData,
  (newVal) => {
    emit("update:formData", { 
      ...newVal,
      existingContactData: existingContact.value || null
    });
    validatePhoneNumberInput();
    validateName();
    emit("validateForm", isFormValid.value);
  },
  { deep: true },
);

watch(existingContact, () => {
  emit("validateForm", isFormValid.value);
});
</script>
